<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <!-- Always use inside OXD-Form -->
  <oxd-input-group v-if="fileSelected || defaultShowAll" :label="label">
    <div class="orangehrm-file-current">
      <div
        v-if="!defaultShowAll"
        class="orangehrm-file-preview"
        @click="downloadFile"
      >
        <oxd-icon class="orangehrm-file-icon" name="file-earmark-text" />
        <oxd-text class="orangehrm-file-name" tag="p" :title="file.filename">
          {{ file.filename }}
          <oxd-icon class="orangehrm-file-download" name="download" />
        </oxd-text>
      </div>
      <div v-if="defaultShowAll" class="orangehrm-file-preview">
        <oxd-icon class="orangehrm-file-icon" name="file-earmark-text" />
        <oxd-text class="orangehrm-file-name" tag="p" title="No File">
          {{ $t('general.no_file') }}
        </oxd-text>
      </div>
      <div v-if="!disabled" class="orangehrm-file-options">
        <oxd-input-field
          type="radio"
          :option-label="$t('general.keep_current')"
          value="keepCurrent"
          :model-value="method"
          @update:model-value="$emit('update:method', $event)"
        />
        <oxd-input-field
          v-if="deletable"
          type="radio"
          :option-label="$t('general.delete_current')"
          value="deleteCurrent"
          :model-value="method"
          @update:model-value="$emit('update:method', $event)"
        />
        <oxd-input-field
          type="radio"
          :option-label="$t('general.replace_current')"
          value="replaceCurrent"
          :model-value="method"
          @update:model-value="$emit('update:method', $event)"
        />
        <oxd-input-field
          v-if="replaceCurrentEmployeeUnsigned"
          type="radio"
          :option-label="$t('general.renew_contract')"
          value="replaceCurrentEmployeeUnsigned"
          :model-value="method"
          @update:model-value="$emit('update:method', $event)"
        />
        <oxd-input-field
          v-if="replaceUsingTemplate"
          class="orangehrm-file-option--template"
          type="radio"
          :option-label="$t('general.replace_with_templates')"
          value="replaceUsingTemplate"
          :model-value="method"
          @update:model-value="$emit('update:method', $event)"
        />
      </div>
    </div>
  </oxd-input-group>
  <div
    v-if="
      method === 'replaceCurrent' ||
      method === 'replaceCurrentEmployeeUnsigned' ||
      (!fileSelected && !defaultShowAll)
    "
    class="orangehrm-file-input"
  >
    <oxd-input-field
      v-bind="$attrs"
      type="file"
      :accept="accept"
      :label="fieldLabel"
      :model-value="newFile"
      :disabled="disabled"
      :placeholder="$t('general.no_file_selected')"
      @update:model-value="$emit('update:newFile', $event)"
    />
    <oxd-text class="orangehrm-input-hint" tag="p">{{ hint }}</oxd-text>
  </div>
  <div
    v-if="method === 'replaceUsingTemplate'"
    class="orangehrm-template-picker"
  >
    <oxd-text tag="p" class="orangehrm-template-picker__title">
      {{ $t('general.replace_with_templates') }}
    </oxd-text>
    <oxd-text
      v-if="!contractTemplate.length"
      tag="p"
      class="orangehrm-template-picker__empty"
    >
      {{ $t('general.no_records_found') }}
    </oxd-text>
    <div v-else class="orangehrm-template-picker__list">
      <div
        v-for="template in contractTemplate"
        :key="template.id"
        role="radio"
        class="orangehrm-template-picker__option"
        :class="{
          'orangehrm-template-picker__option--selected':
            isTemplateSelected(template),
        }"
        :aria-checked="isTemplateSelected(template)"
        :tabindex="disabled ? -1 : 0"
        @click="selectTemplate(template.id)"
        @keydown.enter.prevent="selectTemplate(template.id)"
        @keydown.space.prevent="selectTemplate(template.id)"
      >
        <span
          class="orangehrm-template-picker__indicator"
          :class="{
            'orangehrm-template-picker__indicator--selected':
              isTemplateSelected(template),
          }"
          aria-hidden="true"
        ></span>
        <span class="orangehrm-template-picker__content">
          <span class="orangehrm-template-picker__name">
            {{ templateDisplayName(template) }}
          </span>
          <span
            v-if="templateDisplaySubtitle(template)"
            class="orangehrm-template-picker__subtitle"
          >
            {{ templateDisplaySubtitle(template) }}
          </span>
        </span>
        <span
          v-if="template.version"
          class="orangehrm-template-picker__version"
        >
          v{{ template.version }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import {OxdIcon} from '@ohrm/oxd';

export default {
  name: 'FileUploadInput',
  components: {
    'oxd-icon': OxdIcon,
  },
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      default: '',
    },
    hint: {
      type: String,
      default: '',
    },
    url: {
      type: [String, Function],
      required: true,
    },
    method: {
      type: String,
      required: true,
    },
    file: {
      type: Object,
      required: false,
      default: () => null,
    },
    newFile: {
      type: Object,
      required: false,
      default: () => null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    deletable: {
      type: Boolean,
      default: true,
    },
    replaceCurrentEmployeeUnsigned: {
      type: Boolean,
      default: false,
    },
    replaceUsingTemplate: {
      type: Boolean,
      default: false,
    },
    contractTemplate: {
      type: Array,
      default: () => [],
    },
    selectedContractTemplate: {
      type: [Number, String],
      default: null,
    },
    defaultShowAll: {
      type: Boolean,
      default: false,
    },
    accept: {
      type: String,
      default: '*/*',
    },
  },
  emits: ['update:method', 'update:newFile', 'update:selectedContractTemplate'],
  data() {
    return {
      localSelectedTemplateId: null,
    };
  },
  computed: {
    fileSelected() {
      return this.file && Object.keys(this.file).length > 0;
    },
    fieldLabel() {
      return (this.method === 'replaceCurrent' ||
        this.method === 'replaceCurrentEmployeeUnsigned') &&
        this.fileSelected
        ? this.$t('general.upload_n_file', {
            fileName: this.label,
          })
        : this.label;
    },
  },
  watch: {
    selectedContractTemplate: {
      immediate: true,
      handler(value) {
        this.localSelectedTemplateId = this.toTemplateId(value);
      },
    },
  },
  methods: {
    downloadFile() {
      let downUrl;
      if (typeof this.url === 'function') {
        downUrl = this.url(this.$props);
      } else {
        if (!this.file?.id) return;
        downUrl = `${window.appGlobal.baseUrl}/${this.url}/${this.file.id}`;
      }
      window.open(downUrl, '_blank');
    },
    toTemplateId(id) {
      if (id == null || id === '') {
        return null;
      }

      const normalized = Number(id);
      return Number.isNaN(normalized) ? null : normalized;
    },
    selectTemplate(templateId) {
      if (this.disabled) {
        return;
      }

      const normalized = this.toTemplateId(templateId);
      this.localSelectedTemplateId = normalized;
      this.$emit('update:selectedContractTemplate', normalized);
    },
    isTemplateSelected(template) {
      return (
        this.localSelectedTemplateId != null &&
        this.localSelectedTemplateId === this.toTemplateId(template.id)
      );
    },
    templateDisplayName(template) {
      return template.description?.trim() || template.contractName;
    },
    templateDisplaySubtitle(template) {
      const description = template.description?.trim();
      const contractName = template.contractName?.trim();

      if (description && contractName && description !== contractName) {
        return contractName;
      }

      return '';
    },
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-file {
  &-current {
    display: flex;
    margin-bottom: 1rem;
  }

  &-icon {
    display: block;
    font-size: 3rem;
    margin-bottom: 0.5rem;
  }

  &-download {
    font-size: 12px;
    margin-left: 10px;
    vertical-align: middle;
  }

  &-name {
    display: block;
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
  }

  &-options {
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;

    ::v-deep(.orangehrm-file-option--template .oxd-radio-wrapper) {
      font-size: 0.75rem;
    }
  }

  &-preview {
    padding: 0.5rem;
    cursor: pointer;
    min-height: 90px;
    min-width: 150px;
    text-align: center;
    border-radius: 0.5rem;
    margin-right: 1rem;
    justify-content: center;
    flex-direction: column;
    display: flex;
    align-items: center;
    border: 1px solid $oxd-interface-gray-lighten-1-color;
    background-color: $oxd-background-pastel-white-color;
  }
}

::v-deep(.--label-right) {
  flex-shrink: 0;
  align-self: center;
}

.orangehrm-template-picker {
  margin-top: 0.25rem;
  font-family: $oxd-font-family;

  &__title {
    margin-bottom: 0.65rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: $oxd-interface-gray-darken-1-color;
  }

  &__empty {
    font-size: 0.875rem;
    color: $oxd-interface-gray-color;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__option {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 0.875rem;
    border: 1px solid $oxd-interface-gray-lighten-1-color;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease;

    &:hover:not(&--selected) {
      border-color: rgba($oxd-primary-one-color, 0.35);
    }

    &--selected {
      border-color: rgba($oxd-primary-one-color, 0.4);
      background-color: rgba($oxd-primary-one-color, 0.04);
    }
  }

  &__indicator {
    flex-shrink: 0;
    width: 1rem;
    height: 1rem;
    border: 2px solid $oxd-interface-gray-color;
    border-radius: 50%;
    transition: border-color 0.15s ease, background-color 0.15s ease,
      box-shadow 0.15s ease;

    &--selected {
      border-color: $oxd-primary-one-color;
      background-color: $oxd-primary-one-color;
      box-shadow: inset 0 0 0 3px $oxd-white-color;
    }
  }

  &__content {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
  }

  &__name {
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.3;
    color: $oxd-interface-gray-darken-2-color;
  }

  &__subtitle {
    font-size: 0.75rem;
    line-height: 1.35;
    color: $oxd-interface-gray-color;
  }

  &__version {
    flex-shrink: 0;
    padding: 0.15rem 0.45rem;
    border-radius: 0.35rem;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: $oxd-interface-gray-darken-1-color;
    background-color: $oxd-interface-gray-lighten-2-color;
  }
}
</style>
